// =============================================================================
// src/features/explore/explore-vote-candidates.ts — Agregados estaduais + rotulo
//   do menu de candidato. O REGISTRO (nomes/ids) vive em ./explore-vote-roster
//   (sem deps, p/ a derivacao poder usa-lo sem ciclo); aqui ficam os calculos que
//   dependem da derivacao (soma estadual de votos, ordenacao p/ o dropdown).
//
// SRP: so agregados/rotulo p/ o menu. Sem DOM, sem Leaflet.
// =============================================================================

import type { ElectoralYear } from '../../app/types';
import { EXPLORE_DATASET } from '../../data';
import { deriveVoteRows, votosValidosDe } from './explore-vote-data';
import {
  buildSyntheticCandidates,
  candidateSelOf,
  type SyntheticCandidate,
} from './explore-vote-roster';
import { formatVotos, formatPercent } from './explore-vote-anim';

// Re-export do roster p/ os consumidores antigos (layer/UI) nao mudarem de import.
export { buildSyntheticCandidates, findCandidate, candidateSelOf } from './explore-vote-roster';
export type { SyntheticCandidate, VoteCandidateSel } from './explore-vote-roster';

/** Agregado estadual de um candidato: votos somados + share do total de validos. */
export interface CandidateAggregate {
  readonly votos: number;
  readonly pct: number;
}

/** Total estadual de votos validos estimados (denominador do share; min 1). */
function totalVotosValidos(): number {
  let total = 0;
  for (const munId of Object.keys(EXPLORE_DATASET.munis)) total += votosValidosDe(munId);
  return Math.max(1, total);
}

/** Agregado estadual de um candidato no ano (votos somados + % do total de validos). */
export function candidateAggregate(year: ElectoralYear, cand: SyntheticCandidate): CandidateAggregate {
  const rows = deriveVoteRows(year, candidateSelOf(cand));
  let votos = 0;
  for (const row of rows.values()) votos += row.votosAbsolutos;
  return { votos, pct: (votos / totalVotosValidos()) * 100 };
}

/** Candidato com seu agregado e a colocacao estadual (1-based). */
export interface RankedCandidate {
  readonly cand: SyntheticCandidate;
  readonly agg: CandidateAggregate;
  readonly posicao: number;
}

/** Candidatos ordenados por votos estaduais (desc), com a colocacao anexada. */
export function rankedCandidates(year: ElectoralYear): readonly RankedCandidate[] {
  const scored = buildSyntheticCandidates()
    .map((cand) => ({ cand, agg: candidateAggregate(year, cand) }))
    .sort((a, b) => b.agg.votos - a.agg.votos);
  return scored.map((s, index) => ({ ...s, posicao: index + 1 }));
}

/** Rotulo do menu, estilo Datapedia: `Nº NOME (PARTIDO) · 283.315 votos · 47,71%`. */
export function candidateLabelOf(ranked: RankedCandidate): string {
  const { cand, agg, posicao } = ranked;
  return `${posicao}º ${cand.nome} (${cand.party}) · ${formatVotos(agg.votos)} votos · ${formatPercent(agg.pct, 2)}`;
}
